/**
 * Accessibility statement (הצהרת נגישות), shown at /accessibility and linked from every public page and
 * the portal. Required by the Equal Rights for Persons with Disabilities (Service Accessibility
 * Adjustments) Regulations, 2013, reg. 35: what was made accessible, to which standard, what is known not
 * to be, the date of the last review, and how to reach the accessibility coordinator.
 *
 * Update `updated` and the "known gaps" list whenever an accessibility review is done, and COORDINATOR
 * whenever the coordinator changes: the regulations require a named person with a phone number.
 */
export const COORDINATOR = {
  he: { name: 'גיא ריבק', phone: '054-2148113' },
  en: { name: 'Guy Ribak', phone: '+972-54-214-8113' },
  email: 'accessibility@zerogap.co.il',
};

export interface StatementSection {
  title: string;
  paras?: string[];
  list?: string[];
}
export interface StatementDoc {
  title: string;
  updated: string;
  intro: string[];
  sections: StatementSection[];
}

const he: StatementDoc = {
  title: 'הצהרת נגישות',
  updated: 'נבדק לאחרונה: 27 באוקטובר 2026',
  intro: [
    'אנחנו רוצים שכל אחד ואחת יוכלו להשתמש ב-ZeroGap, כולל אנשים עם מוגבלות. הדף הזה מסביר מה עשינו כדי שהשירות יהיה נגיש, מה עוד לא נגיש, ואיך פונים אלינו אם משהו לא עובד בשבילכם.',
  ],
  sections: [
    {
      title: 'על מה ההצהרה חלה',
      paras: ['ההצהרה חלה על האתר zerogap.co.il, על אזור הלקוחות (הפורטל) ועל דפי הקבצים המשותפים בקישור.'],
    },
    {
      title: 'רמת הנגישות',
      paras: [
        'השירות נבנה כדי לעמוד בתקן הישראלי ת"י 5568, המבוסס על הנחיות WCAG 2.0 ברמה AA, בהתאם לתקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג-2013.',
      ],
    },
    {
      title: 'מה עשינו',
      list: [
        'אפשר להשתמש בכל הפעולות במקלדת בלבד, והמיקוד במקלדת מסומן בבירור.',
        'חלונות קופצים מעבירים אליהם את המיקוד, שומרים אותו בתוכם, ומחזירים אותו למקום שבו הייתם כשהם נסגרים. מקש Esc סוגר אותם.',
        'הניגודיות בין הטקסט לרקע עומדת ביחס של 4.5:1 לפחות, במצב בהיר ובמצב כהה.',
        'הדפים כתובים בעברית מימין לשמאל, ואפשר לעבור לאנגלית בכל דף.',
        'כותרות, תוויות לשדות וסימון סטטוסים מאפשרים שימוש עם קורא מסך.',
        'מצב לא מוצג רק בצבע: לכל סטטוס יש גם מילים.',
        'אפשר להגדיל את הטקסט בדפדפן עד 200% בלי לאבד תוכן.',
        'האתר מכבד את הגדרת המערכת להפחתת תנועה ושקיפות.',
      ],
    },
    {
      title: 'מה עוד לא נגיש',
      paras: ['אנחנו ממשיכים לבדוק ולתקן. ידוע לנו על הדברים הבאים, ואנחנו עובדים עליהם:'],
      list: [
        'במסכים צרים, חלון "שאל את ZeroGap" עלול להסתיר כפתורים בתחתית הדף. אפשר לסגור אותו בכפתור ×.',
        'בחלק מהטבלאות יש טקסט משני קטן (12 פיקסלים).',
        'עדיין לא בוצעה בדיקה מלאה עם קוראי מסך ועם משתמשים עם מוגבלות.',
      ],
    },
    {
      title: 'נתקלתם בבעיה? דברו איתנו',
      paras: [
        'אם משהו באתר לא נגיש לכם, או שיש לכם הצעה, נשמח לשמוע. כדאי לכתוב באיזה דף זה קרה ומה ניסיתם לעשות. נחזור אליכם תוך 5 ימי עבודה.',
        `רכז/ת הנגישות: ${COORDINATOR.he.name}`,
        `טלפון: ${COORDINATOR.he.phone}`,
        `דוא"ל: ${COORDINATOR.email}`,
      ],
    },
  ],
};

const en: StatementDoc = {
  title: 'Accessibility statement',
  updated: 'Last reviewed: 27 October 2026',
  intro: [
    'We want everyone to be able to use ZeroGap, including people with disabilities. This page explains what we have done to make the service accessible, what is not accessible yet, and how to reach us if something does not work for you. The Hebrew version is the original.',
  ],
  sections: [
    {
      title: 'What this statement covers',
      paras: ['This statement covers the zerogap.co.il website, the customer area (the portal) and the pages for files shared by link.'],
    },
    {
      title: 'Accessibility level',
      paras: [
        'The service is built to meet Israeli Standard IS 5568, which is based on WCAG 2.0 level AA, as required by the Equal Rights for Persons with Disabilities (Service Accessibility Adjustments) Regulations, 2013.',
      ],
    },
    {
      title: 'What we have done',
      list: [
        'Everything can be done with a keyboard alone, and keyboard focus is clearly marked.',
        'Dialogs move focus into themselves, keep it inside, and return it to where you were when they close. Esc closes them.',
        'Text contrast is at least 4.5:1 against its background, in both light and dark mode.',
        'Pages are written in Hebrew, right to left, and you can switch to English on any page.',
        'Headings, field labels and status markers support screen readers.',
        'No state is shown by colour alone: every status also has words.',
        'You can enlarge text in the browser up to 200% without losing content.',
        'The site follows your system setting for reduced motion and transparency.',
      ],
    },
    {
      title: 'What is not accessible yet',
      paras: ['We keep testing and fixing. We know about the following and are working on them:'],
      list: [
        'On narrow screens, the "Ask ZeroGap" panel can cover buttons at the bottom of the page. You can close it with the × button.',
        'Some tables have small secondary text (12 pixels).',
        'A full review with screen readers and with users with disabilities has not been done yet.',
      ],
    },
    {
      title: 'Found a problem? Talk to us',
      paras: [
        'If something on the site is not accessible to you, or you have a suggestion, we would like to hear it. Please tell us which page it happened on and what you were trying to do. We will get back to you within 5 working days.',
        `Accessibility coordinator: ${COORDINATOR.en.name}`,
        `Phone: ${COORDINATOR.en.phone}`,
        `Email: ${COORDINATOR.email}`,
      ],
    },
  ],
};

export const STATEMENT: Record<'he' | 'en', StatementDoc> = { he, en };
